// Buttons module client behaviour:
//  - Custom type: mark the button whose link matches the current page as active
//    (so the Active Button Colour / Active Text Colour styling applies).
//  - Custom 2 (price-tier bar): arrow navigation, overflow detection and
//    current-page marking (dark bottom line, no wash — see buttons.less).

function normalisePath(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

// "Custom 2" price-tier bar (ported from the approved raffi-price-selector handoff).
function initPriceTierBar(root: HTMLElement): void {
  const track = root.querySelector<HTMLElement>('.js-btn-tier-track');
  const previous = root.querySelector<HTMLButtonElement>('.js-btn-tier-previous');
  const next = root.querySelector<HTMLButtonElement>('.js-btn-tier-next');
  const links = Array.from(root.querySelectorAll<HTMLAnchorElement>('.js-btn-tier-link'));
  if (!track || !previous || !next || !links.length) return;

  const behavior = (): ScrollBehavior =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  function sync(): void {
    const maximum = Math.max(0, track!.scrollWidth - track!.clientWidth);
    previous!.disabled = maximum <= 2 || track!.scrollLeft <= 2;
    next!.disabled = maximum <= 2 || track!.scrollLeft >= maximum - 2;
    track!.dataset.canScrollRight = String(!next!.disabled);
  }

  function measure(): void {
    if (!root.getClientRects().length) return;
    // Measure with the arrows removed so a fitting desktop row stays stable.
    const position = track!.scrollLeft;
    previous!.hidden = next!.hidden = true;
    const overflow = track!.scrollWidth > track!.clientWidth + 2;
    previous!.hidden = next!.hidden = !overflow;
    track!.scrollLeft = position;
    sync();
  }

  // Left edge of each tier relative to the track's scroll origin (arrow stops).
  function positions(): number[] {
    const inset = parseFloat(getComputedStyle(track!).paddingLeft) || 0;
    return links.map((link) => Math.max(0, link.offsetLeft - inset));
  }

  function advance(direction: number): void {
    const stops = positions();
    const target =
      direction > 0
        ? stops.find((stop) => stop > track!.scrollLeft + 3)
        : stops.reverse().find((stop) => stop < track!.scrollLeft - 3);
    if (target !== undefined) track!.scrollTo({ left: target, behavior: behavior() });
  }

  previous.addEventListener('click', () => advance(-1));
  next.addEventListener('click', () => advance(1));
  track.addEventListener('scroll', sync, { passive: true });

  // Mark the tier matching the current page.
  const current = normalisePath(window.location.pathname);
  let activeLink: HTMLAnchorElement | undefined;
  links.forEach((link) => {
    if (normalisePath(link.pathname) === current) {
      link.classList.add('is-active');
      link.setAttribute('aria-current', 'page');
      activeLink = link;
    }
  });

  let frame = 0;
  const scheduleMeasure = (): void => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(measure);
  };
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(scheduleMeasure).observe(root);
  } else {
    window.addEventListener('resize', scheduleMeasure);
  }

  measure();

  // Scroll the active tier into view on load (matters on mobile where only two fit).
  if (activeLink) {
    const viewport = track.getBoundingClientRect();
    const bounds = activeLink.getBoundingClientRect();
    if (bounds.left < viewport.left + 2 || bounds.right > viewport.right - 2) {
      track.scrollTo({ left: positions()[links.indexOf(activeLink)], behavior: 'auto' });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll<HTMLElement>('.js-btn-tier').forEach(initPriceTierBar);

  const btns = Array.from(
    document.querySelectorAll<HTMLAnchorElement>('.js-buttons-custom-btn')
  );
  if (!btns.length) return;

  const current = window.location.pathname + window.location.search;

  btns.forEach((btn) => {
    // btn.pathname/.search are resolved from the (possibly relative) href by the browser.
    const target = btn.pathname + btn.search;
    if (btn.href === window.location.href || target === current) {
      btn.classList.add('is-active');
    }
  });
});
